import React, { useContext, useState, useEffect, useRef } from "react";
import styled from "styled-components";

import { flow, map, uniq, filter, includes, flatMap } from "lodash/fp";

import PagedModal, { NavigationItem } from "components/PagedModal";
import Dropdown from "components/Dropdown";

import StoreContext from "context/Store";
import { extractCategoryTiers } from "../../adapters";

import { CollectionMenu } from "./Menu";

import { getCollections, getOptionGroups, translateSize } from "utils/product";

import { SIZE_LOCALES } from "constants/product";
import { COLOR_CATEGORIES } from "constants/collection";
import { graphql, useStaticQuery } from "gatsby";

const DropdownWrapper = styled.div`
  margin: 20px;
`;
const ColorList = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  grid-gap: 4px;
  margin: 8px;
`;

const FilterModal = ({ title, products, handle }) => {
  const {
    collection: {
      filters: activeFilters,
      setFilters: setActiveFilters,
      clearFilters: clearActiveFilters,
      filterModalActive: active,
      setFilterModalActive: setActive,
      sizeLocale,
      setSizeLocale,
    },
  } = useContext(StoreContext);
  
  const { allShopifyProduct } = useStaticQuery(graphql`
    query {
      allShopifyProduct {
        edges {
          node {
            tags
          }
        }
      }
    }
  `);

  const collectionScrollContentRef = useRef();
  const catTiers = extractCategoryTiers(allShopifyProduct?.edges);
  const [_force, setForce] = useState(false);
  const [subPage, setSubPage] = useState();
  const [filters, setFilters] = useState(activeFilters);

  const allCollections = getCollections(products);

  const vendors = flow(map("vendor"), uniq)(products);

  const collections = flow(
    filter((c) => !includes(c.title)(vendors)),
    filter((c) => c.title !== title)
  )(allCollections);

  const designers = flow(filter((c) => includes(c.title)(vendors)))(
    allCollections
  );
  const [page, setPage] = useState(1);
  const optionGroups = getOptionGroups(products);
  const [scrollHeight, setScrollHeight] = useState();
  const [openItem, setOpenItem] = useState();
  const onItemClick = (item) => {
    setOpenItem(item === openItem ? false : item);
  };
  useEffect(() => {
    const { current } = collectionScrollContentRef;

    if (current && openItem) {
      const max = 420;
      setScrollHeight(current.clientHeight > max ? max : current.clientHeight);
    }
  }, [openItem]);
  useEffect(() => {
    setFilters(activeFilters);
  }, [activeFilters]);

  const toggleFilter = (filter) => {
    setFilters((item) => {
      if (item.includes(filter)) {
        const newItem = item.filter((f) => f !== filter);
        return newItem;
      } else {
        return [...item, filter];
      }
    });

    setForce((prev) => !prev);
  };

  const onSizeLocaleChange = (option) => {
    setSizeLocale(option);
  };

  const onClose = () => {
    setActive(false);
  };

  const onClear = () => {
    clearActiveFilters();
    setActive(false);
  };

  const onApply = () => {
    setActiveFilters(filters);
    setActive(false);
  };

  const colorList = ({ groupName }) => {
    if (groupName !== "Color") {
      return null;
    }

    return Object.keys(COLOR_CATEGORIES).map((value, index) => {
      const selectedValue = `variant:${groupName}:${value}`;
      const selectedValues = Object.values(
        COLOR_CATEGORIES[value].map((color) => `variant:${groupName}:${color}`)
      );

      const f = `variant:${groupName}:${value}`;

      return (
        <NavigationItem
          title={value}
          type="check"
          active={filters.includes(f)}
          onClick={() => selectedValues.map((item) => toggleFilter(item))}
          key={index}
          groupName={groupName}
        />
      );
    });
  };

  const renderSubPage = () => {
    if (!subPage) return;

    if (subPage === "categories") {
      return (
        <DropdownWrapper>
          <CollectionMenu
            scrollHeight={scrollHeight}
            onItemClick={onItemClick}
            catTiers={catTiers}
            filters={filters}
            openItem={openItem}
            collectionScrollContentRef={collectionScrollContentRef}
            toggleFilter={toggleFilter}
            handle={handle}
            setSubPage={setSubPage}
            setPage={setPage}
            setActive={setActive}
          />
        </DropdownWrapper>
      );
    } else if (subPage === "designers") {
      return designers.map((collection, i) => {
        const f = `collection:${collection.handle}:${collection.title}`;

        return (
          <NavigationItem
            title={collection.title.replace("'", "’").replace(title, "")}
            active={filters.includes(f)}
            onClick={() => toggleFilter(f)}
            type="check"
            key={i}
          />
        );
      });
    } else if (subPage.match(/^group/)) {
      const index = parseInt(subPage.split(":")[1]),
        group = optionGroups[index];

      return (
        <>
          {group.name === "Color" ? (
            <ColorList>{colorList({ groupName: group.name })} </ColorList>
          ) : (
            group.name.match(/size/i) && (
              <DropdownWrapper>
                <Dropdown
                  isSearchable={false}
                  onChange={onSizeLocaleChange}
                  options={SIZE_LOCALES}
                  value={sizeLocale}
                />
              </DropdownWrapper>
            )
          )}

          {group.values.map((value, i) => {
            if (group.name === "Color") {
              return null;
            }
            const display = translateSize(
              value,
              sizeLocale.value,
              group.productType
            );
            const f = `variant:${group.name}:${value}`;

            return (
              <NavigationItem
                title={display}
                type="check"
                active={filters.includes(f)}
                onClick={() => toggleFilter(f)}
                key={i}
              />
            );
          })}
        </>
      );
    }
  };

  useEffect(() => {
    setSubPage("categories");
  }, [setSubPage]);
  return (
    <PagedModal
      title="Filter"
      confirmable
      cancelable
      confirmText="Apply"
      cancelText="Clear"
      onCancel={onClear}
      onConfirm={onApply}
      active={active}
      onCloseClick={onClose}
      page={page}
      setPage={setPage}
    >
      <>
        <NavigationItem
          title="Categories"
          type="page"
          to={2}
          onClick={() => setSubPage("categories")}
        />
        <NavigationItem
          title="Designers"
          type="page"
          to={2}
          onClick={() => setSubPage("designers")}
        />

        {optionGroups.map((group, i) => (
          <NavigationItem
            title={group.name}
            type="page"
            to={2}
            key={i}
            onClick={() => setSubPage(`group:${i}`)}
          />
        ))}
      </>
      <>{renderSubPage()}</>
    </PagedModal>
  );
};

export default FilterModal;